import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../images/logo/logo.png";
import MobileMenu from "../MobileMenu/MobileMenu";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { useLanguage } from "../../context/LanguageContext";

const Header: React.FC = () => {
  const { t } = useLanguage();
  const [isSticky, setIsSticky] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [mobileActive, setMobileActive] = useState(false);
  const lastScrollY = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      // Scroll up → show sticky smoothly
      if (currentScroll < lastScrollY.current && currentScroll > 100) {
        setIsSticky(true);
        setTimeout(() => setIsVisible(true), 10);
      }
      // Scroll down → hide sticky instantly
      else if (currentScroll > lastScrollY.current) {
        setIsVisible(false);
        setTimeout(() => setIsSticky(false), 100);
      }

      // Reset if near top
      if (currentScroll <= 100) {
        setIsSticky(false);
        setIsVisible(false);
      }

      lastScrollY.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileActive(false);
  };

  return (
    <header
      id="xb-header-area"
      className="header-area header-style--one header-transparent is-sticky"
    >
      <div
        className={`xb-header xb-sticky-stt ${
          isSticky ? "xb-header-area-sticky" : ""
        } ${isVisible ? "xb-header-fixed" : "xb-header-hidden"}`}
      >
        <div className="container mxw-1650">
          <div className="header__wrap ul_li_between">
            {/* Logo */}
            <div className="xb-header-logo">
              <Link to="/" className="logo1" onClick={handleClick}>
                <img
                  src={logo}
                  alt={t.company.name}
                  className="img-fluid"
                  style={{ height: "60px", width: "auto" }}
                />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="main-menu__wrap navbar navbar-expand-lg p-0">
              <nav className="main-menu collapse navbar-collapse">
                <ul>
                  <li>
                    <Link to="/" onClick={handleClick}>
                      <span>{t.nav.home}</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/about" onClick={handleClick}>
                      <span>{t.nav.about}</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/service-details" onClick={handleClick}>
                      <span>{t.nav.services}</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/project" onClick={handleClick}>
                      <span>{t.nav.projects}</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/contact" onClick={handleClick}>
                      <span>{t.nav.contact}</span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Desktop CTA & Language Switcher (Hidden on Mobile) */}
            <div className="header-btn d-none d-lg-flex align-items-center gap-3">
              <LanguageSwitcher />
              <Link to="/contact" className="thm-btn" onClick={handleClick}>
                {t.nav.consultation}
              </Link>
            </div>

            {/* Mobile Controls: Compact Language Switcher + Hamburger Toggle (< 992px) */}
            <div
              className="header-bar-mobile side-menu d-lg-none"
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <LanguageSwitcher isMobile={true} />
              <button
                className="xb-nav-mobile"
                onClick={() => setMobileActive(!mobileActive)}
                aria-label="Toggle navigation menu"
              >
                <i className="far fa-bars"></i>
              </button>
            </div>
          </div>

          {/* Mobile Drawer Menu */}
          <div className="xb-header-wrap">
            <div className={`xb-header-menu ${mobileActive ? "active" : ""}`}>
              <div className="xb-header-menu-scroll">
                <div
                  className="xb-menu-close xb-hide-xl xb-close"
                  onClick={() => setMobileActive(false)}
                ></div>

                <div className="xb-logo-mobile xb-hide-xl">
                  <Link to="/" rel="home" onClick={handleClick}>
                    <img src={logo} alt={t.company.name} />
                  </Link>
                </div>

                {/* Mobile Language Switcher Inside Drawer */}
                <div
                  className="xb-header-mobile-lang xb-hide-xl"
                  style={{
                    padding: "20px 25px 10px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <LanguageSwitcher isMobile={true} />
                </div>

                {/* Navigation Links */}
                <nav className="xb-header-nav">
                  <MobileMenu onItemClick={() => setMobileActive(false)} />
                </nav>

                {/* Mobile Drawer CTA Button */}
                <div style={{ padding: "20px 25px 30px" }}>
                  <Link
                    to="/contact"
                    className="thm-btn w-100 text-center"
                    onClick={handleClick}
                  >
                    {t.nav.consultation}
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="xb-header-menu-backdrop"
              onClick={() => setMobileActive(false)}
            ></div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
